import * as React from 'react';

import _ from 'underscore';

import {
    fetchFunnelReportData,
    createUrlFromNutshellFilterObject,
    createNutshellFilterObjectFromUrl,
    deserializeUrlParam,
} from 'nutshell-core';

import * as Leads from 'nutshell-core/leads';
import {Routing} from 'nutshell-core/routing';
import * as Schema from 'nutshell-core/schema';
import type {UnserializedFilterObject} from 'nutshell-core/types';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {RelativeOverlay} from 'shells/relative-overlay/relative-overlay';

import {readLocalStorage, writeLocalStorage} from '../../../util/localStorage';

import {StagesEmpty} from '../../base/empty-states/stages-empty';
import {FeatureGatePageWrapper} from '../../limits/feature-gates/feature-gate-page-wrapper';
import {OnboardingFunnelReportModal} from '../../onboarding/onboarding-funnel-report-modal';

import {AiGeneratedBanner} from '../banner/ai-generated-banner';
import type {ReportFacet, ReportURLParams} from '../report-types';

// @ts-expect-error
import emptyJson from './empty-funnel';
import {FunnelReportDisplayTypeSelector} from './funnel-report-display-type-selector';
import {FunnelReportFilters} from './funnel-report-filters';
// @ts-expect-error
import {FunnelReportTotals} from './funnel-report-totals';
// @ts-expect-error
import {FunnelReportView} from './funnel-report-view';
import type {Stageset} from './types';

type FunnelReportProps = {
    urlParams: ReportURLParams<'leads'>;
    location?: {
        search: string;
    };
    onParamChange?: (arg1: any, shouldMerge?: boolean | null | undefined) => any;
};

type FunnelReportState = {
    isLoading: boolean;
    isUpdating: boolean;
    shouldPersist: boolean;
    filters: UnserializedFilterObject<any>;
    json: any;
    reportFacet: ReportFacet;
    showFunnelOnboardingModal: boolean;
    hasStages?: boolean;
    error: Error | null | undefined;
};

export class FunnelReport extends React.Component<FunnelReportProps, FunnelReportState> {
    // @ts-expect-error
    httpConfig: {
        baseUrl: string;
        isWebRequest: boolean;
    };

    // @ts-expect-error
    schema: Schema.Properties;
    // @ts-expect-error
    mounted: boolean;

    constructor(props: FunnelReportProps) {
        super(props);
        this.state = {
            isLoading: true,
            isUpdating: false,
            shouldPersist: true,
            filters: {},
            json: emptyJson,
            reportFacet: 'count',
            showFunnelOnboardingModal: false,
            error: undefined,
        };
    }

    componentDidMount() {
        document.title = 'Funnel Report · Nutshell';
        this.httpConfig = {
            baseUrl: '/rest/',
            isWebRequest: true,
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        NutClientConfig.hasFunnelReport &&
            Leads.fetchSchema().then((schemaResponse) => {
                this.schema = schemaResponse.properties;
                this.fetchFunnelReportDataAndUpdateFilters(this.getPersistedFilters(this.schema));
            });

        this.mounted = true;
    }

    UNSAFE_componentWillReceiveProps(nextProps: FunnelReportProps) {
        if (!nextProps.location || !this.props.location) return;

        if (nextProps.location.search !== this.props.location.search) {
            this.setState({isUpdating: true});

            if (nextProps.location) {
                const filters = this.getPersistedFilters(this.schema, nextProps.location.search);
                this.fetchFunnelReportDataAndUpdateFilters(filters, false);
            }
        }
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    render() {
        const hasFunnelFilter =
            this.state.filters && Object.keys(this.state.filters).includes('stageset');
        const selectedFunnelId =
            hasFunnelFilter &&
            this.state.filters.stageset &&
            Array.isArray(this.state.filters.stageset.data) &&
            this.state.filters.stageset.data[0] &&
            typeof this.state.filters.stageset.data[0] === 'object' &&
            typeof this.state.filters.stageset.data[0].data === 'string'
                ? this.state.filters.stageset.data[0].data
                : undefined;

        const stagesets: Stageset[] | undefined =
            this.schema && this.schema.stageset && this.schema.stageset.type === 'enum'
                ? (this.schema.stageset.enum as any) // Only way I can get flow to realize this isn't a string
                : undefined;

        return NutClientConfig.hasFunnelReport ? (
            <div>
                {/* Extra safety, ran into some issues in dev which gave the impression that report isn't always defined */}
                {this.props.urlParams.report && this.props.urlParams.report.id && (
                    <AiGeneratedBanner reportId={this.props.urlParams.report.id} />
                )}
                <div className='ui-funnel-report'>
                    <div className='ui-funnel-report-container' style={{position: 'relative'}}>
                        <div className='ui-funnel-report__subheader'>
                            <FunnelReportDisplayTypeSelector
                                multipleFunnelEnabled={NutCapabilities.multipleFunnel.isAvailable}
                                reportFacet={this.state.reportFacet}
                                stagesets={stagesets}
                                selectedFunnelId={selectedFunnelId}
                                onStagesetChanged={this.handleStagesetChanged}
                                onReportFacetChanged={this.handleReportFacetChanged}
                            />
                            <div className='ui-funnel-report__help-btn-container'>
                                <a
                                    className='icon icon-help'
                                    onClick={() => this.setState({showFunnelOnboardingModal: true})}
                                />
                            </div>
                        </div>
                        {this.state.isLoading ? (
                            <div
                                id='report-header-options'
                                className='ui-funnel-report__filters-container'
                                style={{height: 31}}
                            >
                                <span className='ui-funnel-report__header-label'>Filter: </span>
                            </div>
                        ) : (
                            <div
                                id='report-header-options'
                                className='ui-funnel-report__filters-container'
                            >
                                <FunnelReportFilters
                                    filters={this.state.filters}
                                    schema={this.schema}
                                    onFilterRemove={this.handleFilterRemove}
                                    onFilterSubmit={this.handleFilterSubmit}
                                />
                                <div className='ui-funnel-report__meta'>
                                    {this.state.isLoading ? undefined : (
                                        <FunnelReportTotals meta={this.state.json.meta} />
                                    )}
                                </div>
                            </div>
                        )}
                        {this.state.error ? (
                            <FlashBanner type='error'>
                                We’re sorry. There was a problem loading the funnel report. Please
                                try refreshing the page or changing your filters
                            </FlashBanner>
                        ) : undefined}
                        {this.renderFunnelMain()}
                        {this.state.isUpdating || this.state.isLoading ? (
                            <RelativeOverlay>
                                <div className='ui-funnel-report__loading-state'>
                                    <LoadingIcon />
                                </div>
                            </RelativeOverlay>
                        ) : undefined}
                    </div>
                    {this.state.showFunnelOnboardingModal ? (
                        <OnboardingFunnelReportModal
                            onDismiss={() => this.setState({showFunnelOnboardingModal: false})}
                        />
                    ) : undefined}
                </div>
            </div>
        ) : (
            <FeatureGatePageWrapper
                header='Unlock insights into your sales performance'
                subHeader='Take your sales strategy to the next level by reviewing your pipeline performance and conversion rates.'
                bullets={[
                    'See where you’re winning the most leads, so you can celebrate successes and do more of what works.',
                    'Focus on what’s important to you by filtering data that fits your unique needs.',
                    'Identify areas to improve your approach, helping you turn potential into profit.',
                ]}
                feature='funnelReport'
            />
        );
    }

    renderFunnelMain = () => {
        if (this.state.hasStages || this.state.isLoading || this.state.error) {
            return (
                <FunnelReportView
                    json={this.state.json}
                    isBlank={this.state.isLoading || Boolean(this.state.error)}
                    // Within the funnel report, we use "amount/count" instead of
                    // "total" and "count", and it's pretty tied to the API,
                    // so not really worth changing right now. This is the translation
                    // layer for that distinction
                    displayType={this.state.reportFacet === 'total' ? 'amount' : 'count'}
                    showReassigned={Object.keys(this.state.filters).includes('owner')}
                />
            );
        }

        return (
            <div className='ui-funnel-report__empty-container'>
                <StagesEmpty
                    titleText='No stages'
                    bodyText='You need to set up your stages to use this report.'
                />
            </div>
        );
    };

    /**
     * This function takes a filter object, and fetches the data from the api, updating
     * component state based on the response.
     * By default, it also updates the url and persists it to localstorage, but that can be disabled.
     *
     * @param  {Object} filters          Unserialized filter object
     * @param  {boolean} shouldUpdateURL Controls whether the URL should be updated to match the provided filters
     * @return {void}
     */
    fetchFunnelReportDataAndUpdateFilters(
        filters: UnserializedFilterObject<any>,
        shouldUpdateURL = true
    ) {
        let filtersHaveChanged = false;
        fetchFunnelReportData(filters, this.schema).then(
            (response) => {
                // If the stageset filter isn't set yet, use the default one that the server provided
                if (!('stageset' in filters)) {
                    filters.stageset = {
                        anyAll: 'any',
                        data: [{data: response.meta.stageset.id}],
                    };
                    filtersHaveChanged = true;
                }

                if (this.mounted) {
                    if (shouldUpdateURL || filtersHaveChanged) {
                        this.updateAndPersistUrl(filters, this.schema);
                    }
                    const {dashboards} = response;
                    const hasNoStages = _.isUndefined(dashboards) || dashboards.length === 0;
                    this.setState({
                        filters: filters,
                        isLoading: false,
                        isUpdating: false,
                        json: response,
                        shouldPersist: true,
                        hasStages: !hasNoStages,
                        error: undefined,
                    });
                }
            },
            (err: Error) => {
                if (this.mounted) {
                    this.setState({
                        error: err,
                        isLoading: false,
                        isUpdating: false,
                        filters: filters,
                        hasStages: true,
                    });
                }
            }
        );
    }

    /**
     * This will return a filter object based on one of the following:
     *  - An explicitly provided `search` parameter
     *  - The page's current `location.search`
     *  - A persisted set of parameters
     *
     * If a search is used, we will avoid persisting it.
     *
     * @param  {Object} schema                   The filter schema
     * @param  {string} [search=location.search] Optional search string to use to create filter object
     * @return {Object}                          An unserialized filter object
     */
    // No idea why eslint is complaining about a flow type here

    getPersistedFilters(schema: Schema.Properties, search: string = location.search) {
        let queryParams = '';

        // Search can contain leading "?"
        const sanitizedQueryParams = search && search.replace(/^\?/, '');

        // If we have a pasted in url, or a page was reloaded with query params, use those
        if (sanitizedQueryParams.length) {
            queryParams = sanitizedQueryParams;

            // We shouldn't persist our filters on the next response, since we're using
            // a custom or pasted url
            this.setState({shouldPersist: false});
        } else {
            // Otherwise, check for persisted params, and if they exist, use those
            const persistedUrlParams = readLocalStorage(location.pathname);
            if (persistedUrlParams) {
                queryParams = persistedUrlParams;
            }
        }

        return createNutshellFilterObjectFromUrl(queryParams, schema);
    }

    // No idea why eslint is complaining about a flow type here

    updateAndPersistUrl(filters: UnserializedFilterObject<any>, schema: Schema.Properties) {
        // This is clunky, converting one kind of filter objects to a serialized string,
        // and then back into a different kind, but it works and I'm not sure how else to do it
        const queryParams = createUrlFromNutshellFilterObject(filters, schema);

        // The "Old" Funnel Report (not in the new reports flow) doesn't
        // use onParamChange, so we need to branch here
        if (this.props.onParamChange) {
            const filterObj = Routing.deparam(queryParams);

            this.props.onParamChange(filterObj, false);
        } else {
            const baseUrl = location.pathname;
            const url = queryParams.length ? `${baseUrl}?${queryParams}` : baseUrl;

            history.replaceState({}, '', url);
        }

        if (this.state.shouldPersist) {
            writeLocalStorage(location.pathname, queryParams);
        }
    }

    handleReportFacetChanged = (reportFacet: ReportFacet) => {
        this.setState({reportFacet: reportFacet});
    };

    handleStagesetChanged = (stageset: Stageset) => {
        this.handleFilterSubmit({
            stageset: deserializeUrlParam(stageset.id, 'enum'),
        });
    };

    handleFilterSubmit = (filterObj: UnserializedFilterObject<any>) => {
        this.setState({isUpdating: true});

        const currentFilters = this.state.filters;
        const newFilters = _.clone(currentFilters);

        // Get filter key
        const filterKey = Object.keys(filterObj)[0];

        // @ts-expect-error
        newFilters[filterKey] = filterObj[filterKey];
        this.fetchFunnelReportDataAndUpdateFilters(newFilters);
    };

    handleFilterRemove = (filterKey: string) => {
        this.setState({isUpdating: true});

        const currentFilters = this.state.filters;
        const newFilters = _.clone(currentFilters);

        delete newFilters[filterKey];
        this.fetchFunnelReportDataAndUpdateFilters(newFilters);
    };
}
